// Per-cohort theme + ritual gating for the founder portal.
//
// Each cohort gets its own URL space at /<slug>/founder/* and pulls brand
// label, footer copy, support contact, and which weekly rituals to render
// from this map keyed by cohort slug. Unknown slugs fall back to the
// `default` theme — generic Favcy Select branding, all rituals on.
//
// When you launch a new cohort, add a row here BEFORE telling founders
// the URL. If you skip this step, founders in the new cohort will see
// the generic fallback shell. Loud-but-acceptable: they can still log
// in, the data is correct, the cohort identity just isn't surfaced.
//
// Keep copy in plain Indian English. No em/en dashes. Numerals as digits.

export type RitualKey =
  | "thread"
  | "readiness"
  | "updates"
  | "proof"
  | "review"
  | "missions"
  | "network"
  | "preview"
  | "ask"
  | "onboarding";

export type CohortTheme = {
  brandName: string;       // sidebar h1, login header brand
  programLabel: string;    // sidebar mono caption (e.g. "1 to 10 · Founder OS")
  footerLabel: string;     // login footer right-side string
  shortLabel: string;      // short cohort label used in body copy
  supportContact: string;  // "Favcy Advisor for the 1to10 cohort"
  loginStyle: "longform" | "compact"; // longform = the 1to10 marketing page; compact = form only
  // Which sidebar items to render. Hidden items still have working routes
  // (so old links don't 404), but they don't show in the nav.
  rituals: Record<RitualKey, boolean>;
};

const ALL_RITUALS_ON: Record<RitualKey, boolean> = {
  thread: true,
  readiness: true,
  updates: true,
  proof: true,
  review: true,
  missions: true,
  network: true,
  preview: true,
  ask: true,
  onboarding: true,
};

// Theme objects defined once, registered under both the clean slug AND the
// legacy DB slug so prod URLs work whether or not the slug-rename SQL has
// been run. Remove the legacy aliases ONLY after
// .local/prod-migrations/2026-05-07-clean-cohort-slugs.sql has been
// applied AND every external link (emails, calendar invites, bookmarks)
// pointing at the legacy URL has been verified migrated. There is no
// expiry date on this — leaving the alias costs nothing; removing it
// while a real founder still has the old bookmark costs trust.
const ONE_TO_TEN_THEME: CohortTheme = {
  brandName: "Favcy Select",
  programLabel: "1 to 10 · Founder OS",
  footerLabel: "Favcy Select - 1to10 cohort",
  shortLabel: "1 to 10",
  supportContact: "Favcy Advisor for the 1to10 cohort",
  loginStyle: "longform",
  rituals: ALL_RITUALS_ON,
};

const MAYDEAL_THEME: CohortTheme = {
  brandName: "Favcy Select",
  programLabel: "May Deal · Founder Workspace",
  footerLabel: "Favcy Select - May Deal cohort",
  shortLabel: "May Deal",
  supportContact: "Favcy Advisor for the May Deal cohort",
  loginStyle: "compact",
  // All rituals on by default. Earlier this turned off Readiness, Updates,
  // Proof, Review, Missions, Network for May Deal on the agent's own
  // assumption that "May Deal is venture_building, not acceleration." That
  // was a product call the agent was not authorised to make and it
  // regressed the founder shell from 10 nav items to 4. PC decides which
  // rituals a cohort gets, not the agent. If May Deal really should hide
  // some rituals, PC will say so explicitly and we'll flip individual
  // booleans below; the default is parity with 1to10.
  rituals: ALL_RITUALS_ON,
};

export const COHORT_THEMES: Record<string, CohortTheme> = {
  "1to10": ONE_TO_TEN_THEME,
  // Legacy DB slug — prod row may still be named this until the slug-rename
  // SQL is applied. Keep the alias so /1to10-by-favcy-25-26/founder/... works.
  "1to10-by-favcy-25-26": ONE_TO_TEN_THEME,
  "maydeal": MAYDEAL_THEME,
  // Legacy DB slug — prod row is currently `may-deal`. Keep the alias so
  // /may-deal/founder/... works until the rename SQL runs and external
  // links are migrated.
  "may-deal": MAYDEAL_THEME,
  default: {
    brandName: "Favcy Select",
    programLabel: "Founder Workspace",
    footerLabel: "Favcy Select",
    shortLabel: "Favcy Select",
    supportContact: "Favcy Advisor",
    loginStyle: "compact",
    rituals: ALL_RITUALS_ON,
  },
};

export function themeForSlug(slug: string): CohortTheme {
  return COHORT_THEMES[slug] ?? COHORT_THEMES.default;
}

// True only for slugs explicitly registered in COHORT_THEMES (excluding the
// "default" fallback key). Use this at route entry to reject made-up cohort
// URLs rather than silently rendering the generic shell — a fallback shell
// looks legitimate to a real founder and hides the misconfiguration. The
// fallback theme remains as a defensive last-resort inside themeForSlug so
// nothing crashes if isKnownSlug is forgotten somewhere.
export function isKnownSlug(slug: string): boolean {
  return slug !== "default" && Object.prototype.hasOwnProperty.call(COHORT_THEMES, slug);
}
